import { Event } from "interfaces/content";

import React from "react";
import { formatDate } from "services/common/dates/dates";
import useLocale from "hooks/common/useLocale/useLocale";
import download from "images/download.svg";
import IconLink from "components/common/ui/IconLink/IconLink";
import { config } from "services/common/config/config";
import useTranslate from "hooks/common/useTranslate/useTranslate";
import styled from "styled-components";
import styles from "styles/styles";
import Button from "../../../common/ui/buttons/Button/Button";
import Collapsible from "components/common/ui/Collapsible/Collapsible";
import moment from "moment";
import { ics } from "calendar-link";
interface Props {
    event: Event;
}

const Container = styled.div`
    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1px dotted ${styles.colors.black};
`;

const LinkContainer = styled.div`
    padding-right: 10px;
`;

const EventBlockContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 20px 0px;
    justify-content: space-between;
    min-height: 59px;
`;

const EventDescription = styled.span`
    display: flex;
    align-items: center;
`;

const EventDescriptionTitle = styled.p`
    font-weight: bold;
    margin: 0 8px 0 17px;
`;

const EventDescriptionLocation = styled.p`
    margin: 0 0 0 8px;
`;

const ExpandedEventBlock = styled.div`
    padding: 0px 20px 10px 45px;
    white-space: pre-wrap;

    p {
        margin: 0;
    }
`;

const FileList = styled.div`
    display: flex;

    button:not(:first-child) {
        margin-left: 20px;
    }
`;
const AddToCalendarLink = styled.a`
    font-style: italic;
    font-size: ${styles.fontSize.smaller};
    color: ${styles.colors.grey7};
`;

const isWithinDay = (event: Event): boolean => {
    const eventDate = moment(event.date);
    const currentDate = moment();

    return eventDate.diff(currentDate, "days") === 0;
};

const EventBlock = ({ event }: Props) => {
    const translate = useTranslate();
    const locale = useLocale();

    if (!event) {
        return <div />;
    }

    const excerpt = (
        <>
            <EventBlockContainer>
                <EventDescription>
                    <EventDescriptionTitle>{event.description?.fi}</EventDescriptionTitle>
                    <p>{formatDate(event.date, locale)}</p>
                    {event.location && (
                        <EventDescriptionLocation>{event.location}</EventDescriptionLocation>
                    )}
                    &nbsp;(
                    <AddToCalendarLink
                        onClick={(e) => e.stopPropagation()}
                        href={ics({
                            title: event.description[locale] ?? "Fingrid Webinar",
                            description: event.weblink,
                            start: event.date,
                            duration: [1, "hour"],
                        })}
                        target="_blank"
                    >
                        {translate("common.addToCalendar")}
                    </AddToCalendarLink>
                    )
                </EventDescription>
                <div>
                    {isWithinDay(event) && !event.isPastEvent && event.weblink && (
                        <Button
                            disabled={!isWithinDay(event)}
                            onClick={() => window.open(event.weblink || "", "_blank")}
                        >
                            {translate("customPage.eventsAndWebinars.participate")}
                        </Button>
                    )}
                </div>
            </EventBlockContainer>
        </>
    );

    return (
        <Container>
            <Collapsible excerpt={excerpt} collapsed={false}>
                <ExpandedEventBlock>
                    <p>{event.details?.[locale] ?? event.details?.fi ?? ""}</p>
                    <FileList>
                        {event.recordingUrl && (
                            <LinkContainer>
                                <IconLink
                                    label={translate("customPage.eventsAndWebinars.recording")}
                                    src={download}
                                    href={event.recordingUrl}
                                    fileDescription={event.description?.fi}
                                    openInNewTab={true}
                                />
                            </LinkContainer>
                        )}
                        {event.documents.map((document) => (
                            <LinkContainer>
                                <IconLink
                                    label={document.title}
                                    src={download}
                                    href={`${config.documents.endpoints.file}${document.mFilesDocument.file.id}`}
                                    fileDescription={document.title}
                                    download={true}
                                />
                            </LinkContainer>
                        ))}
                    </FileList>
                </ExpandedEventBlock>
            </Collapsible>
        </Container>
    );
};

export default EventBlock;
